import MeiliSearch, { Index } from "meilisearch";

import {
  OmniSearch,
  useMeiliSearchAdapter,
  type OmniSearchResult,
} from "@filadd/phi-plus-layout";
import { css } from "@filadd/phi-plus-styled-system/css";

import { useRudderstackTrack } from "hooks/rudderstack";
import { useRouter } from "next/router";
import { useCallback, useState } from "react";
import type { AccessType } from "types/users/release";

interface Props {
  courseSlug: string;
  accessType: AccessType;
}

export const Search = ({ courseSlug, accessType }: Props) => {
  const enableOmniSearch = accessType !== "NO_ACCESS";
  const meiliHost = process.env.MEILISEARCH_HOST_URL;
  const meiliApiKey = process.env.MEILISEARCH_API_KEY;
  const indexName = process.env.OMNI_SEARCH_INDEX;
  let index: Index<OmniSearchResult> | undefined;

  if (enableOmniSearch && meiliHost && meiliApiKey && indexName) {
    const meiliSearchClient = new MeiliSearch({
      host: meiliHost,
      apiKey: meiliApiKey,
    });
    index = meiliSearchClient.index(indexName);
  }

  const { locale } = useRouter();
  const trackOpen = useRudderstackTrack("CONTENT_SEARCH_USER_OPENED_MODAL", {
    courseSlug,
    locale,
  });
  const trackSearch = useRudderstackTrack("CONTENT_SEARCH_USER_SEARCHED", {
    courseSlug,
    locale,
  });
  const trackClick = useRudderstackTrack("CONTENT_SEARCH_USER_CLICKED_RESULT", {
    courseSlug,
    locale,
  });
  const [previousSearchEvent, setPreviousSearchEvent] = useState<Record<
    string,
    unknown
  > | null>(null);

  const omniSearchAdapter = useMeiliSearchAdapter({
    index,
    courseSlug,
    searchParams: {
      attributesToHighlight: ["title"],
    },
    enabled: enableOmniSearch,
    onOpen: trackOpen,
    onSearch: useCallback(
      (query: string, trigger: string, resultCount: number) => {
        const searchEvent = {
          searchQuery: query,
          searchTrigger: trigger,
          searchResultsAmount: resultCount,
        };
        trackSearch(searchEvent);
        setPreviousSearchEvent(searchEvent);
      },
      [trackSearch],
    ),
    onResultClick: useCallback(
      (type: string, text: string, result?: object) =>
        trackClick({
          ...previousSearchEvent,
          resultType: type,
          resultText: text,
          result,
        }),
      [previousSearchEvent, trackClick],
    ),
  });

  return enableOmniSearch ? (
    <div
      className={css({
        "& input": { bg: "background" },
        "& em": { fontStyle: "unset" },
      })}
    >
      <OmniSearch {...omniSearchAdapter} />
    </div>
  ) : null;
};
